import { useState } from 'react'
import { FaCheck } from 'react-icons/fa6'
import { CiEdit } from 'react-icons/ci'
import { useNavigate, useParams } from 'react-router-dom'
import posthog from 'posthog-js'
import HashtagText from 'components/hashtagText'
import { faPlain, longJalali, money } from 'utilities/helper/format'
import useGetMoneyPoolManagement from 'pages/fund/fundPage/hooks/useGetMoneyPoolManagement'
import { InstallmentTiming } from '../selection'

/**
 * Characters past which the note is clamped to two lines.
 *
 * A count, not a measured overflow: the same heuristic `CandidateRow` uses for
 * the loan-request note, so the two places a manager's free text is quoted
 * behave identically. The schedule is a rail of equal-height nodes and a
 * three-hundred-character note left unclamped turns one row into most of the
 * screen — which is exactly the row the reader was NOT looking for.
 */
const NOTE_CLAMP = 90

interface IItemProps {
    /** the installment's own 1-based number in the schedule */
    number: number
    jalaliDate: string
    amount: number
    isOverdue: boolean
    isPayed: boolean
    /** how the repayment landed against the due date; null when unknowable */
    timing: InstallmentTiming | null
    /** the manager's note on the recorded repayment; '' when there is none */
    note?: string
    id: number
    /** unpaid, and therefore addable to the payment */
    isSelectable: boolean
    isSelected: boolean
    /** false on the first/last row — the rail must not run off either end */
    hasPrev: boolean
    hasNext: boolean
    /** the rail segment to the neighbour is part of the settled/selected run */
    linkAbove: boolean
    linkBelow: boolean
    onSelect: () => void
}

/**
 * One installment on the schedule rail.
 *
 * The rail is the point of the screen. A قرض‌الحسنه schedule is settled
 * front-to-back, so the installments are not twelve independent cards — they
 * are one line the member is walking down. Paid nodes are filled; selecting an
 * installment fills its node too, which means **choosing installments looks
 * exactly like paying them**, and the run you have selected reads as a
 * continuation of the run you have already settled rather than as a scatter of
 * ticked boxes.
 *
 * This replaces a force-LTR transcription of the Android row (`قسط N` boxed
 * top-right, amount centred, hard-coded hexes) that predates the home page and
 * صندوق‌ها redesigns. Native RTL, tokens only — same rule as `modules/funds/card`.
 */
export default function Item({
    number,
    jalaliDate,
    amount,
    isOverdue,
    isPayed,
    timing,
    note,
    id,
    isSelectable,
    isSelected,
    hasPrev,
    hasNext,
    linkAbove,
    linkBelow,
    onSelect,
}: IItemProps) {
    const { moneyPool } = useParams()
    const navigate = useNavigate()
    const { moneyPoolManagement } = useGetMoneyPoolManagement(moneyPool)

    const canEdit =
        moneyPoolManagement?.poolship?.role === 'mgr' ||
        moneyPoolManagement?.poolship?.role === 'own'

    const filled = isPayed || isSelected

    const [showFullNote, setShowFullNote] = useState(false)
    const trimmedNote = note?.trim() ?? ''
    const isLongNote = trimmedNote.length > NOTE_CLAMP

    const toggleNote = () => {
        // Only the opening half is worth counting: it is the one that says
        // the clamped two lines were not enough.
        if (!showFullNote) {
            posthog.capture('installment_note_expanded', {
                moneypool_id: moneyPool,
                installment_index: number,
                note_length: trimmedNote.length,
            })
        }
        setShowFullNote(!showFullNote)
    }

    const select = () => {
        if (isSelectable) onSelect()
    }

    return (
        // The whole row is the tap target, rail included. The node used to be
        // decoration next to a clickable card, so tapping the empty circle —
        // the one control on the row that actually *looks* like a checkbox —
        // did nothing at all. Nothing inside is focusable, so a single
        // checkbox role over both halves is also the right thing for a screen
        // reader; the edit pencil stops propagation and keeps its own.
        <div
            role={isSelectable ? 'checkbox' : undefined}
            aria-checked={isSelectable ? isSelected : undefined}
            aria-label={isSelectable ? `قسط ${number}` : undefined}
            tabIndex={isSelectable ? 0 : undefined}
            onClick={select}
            onKeyDown={(event) => {
                if (!isSelectable) return
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    select()
                }
            }}
            className={`flex items-stretch gap-3 rounded-card focus:outline-none focus-visible:ring-2 focus-visible:ring-green-1 ${
                isSelectable ? 'cursor-pointer' : ''
            }`}
        >
            {/* The rail — right side, because the page reads right to left. */}
            <div className="relative w-6 shrink-0 flex justify-center">
                {hasPrev && (
                    <span
                        className={`absolute top-0 h-6 w-[2px] ${
                            linkAbove ? 'bg-green-1' : 'bg-divider'
                        }`}
                        aria-hidden="true"
                    />
                )}
                {hasNext && (
                    <span
                        className={`absolute top-6 bottom-0 w-[2px] ${
                            linkBelow ? 'bg-green-1' : 'bg-divider'
                        }`}
                        aria-hidden="true"
                    />
                )}
                <span
                    data-testid={`installment-node-${number}`}
                    className={`relative mt-4 h-5 w-5 shrink-0 rounded-full border-2 flex items-center justify-center transition-colors duration-200 motion-reduce:transition-none ${
                        filled
                            ? 'border-green-1 bg-green-1'
                            : isOverdue
                            ? 'border-danger bg-white'
                            : 'border-stroke bg-white'
                    }`}
                    aria-hidden="true"
                >
                    {filled && <FaCheck className="h-2.5 w-2.5 text-white" />}
                </span>
            </div>

            <div
                className={`flex-1 min-w-0 mb-2 rounded-card border p-3 text-right transition-colors duration-200 motion-reduce:transition-none ${
                    isSelected
                        ? 'border-green-1 bg-brand-soft'
                        : 'border-divider bg-white'
                } ${
                    isPayed
                        ? 'opacity-70'
                        : isSelectable
                        ? 'active:scale-[0.995]'
                        : ''
                }`}
            >
                <div className="flex items-baseline justify-between gap-3">
                    <span
                        className={`text-[15px] font-bold shrink-0 ${
                            isSelected ? 'text-brand-deep' : 'text-heading'
                        }`}
                    >
                        قسط {faPlain.format(number)}
                    </span>
                    <span className="text-[15px] font-medium text-text-body truncate">
                        {money(amount)} تومان
                    </span>
                </div>

                <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-[13px] text-text-secondary truncate">
                        {longJalali(jalaliDate)}
                    </span>

                    <div className="flex items-center gap-2 shrink-0">
                        {isPayed ? (
                            <span className="rounded-full bg-divider px-2.5 py-[2px] text-xs font-bold text-text-secondary">
                                پرداخت شده
                            </span>
                        ) : isOverdue ? (
                            <span className="rounded-full bg-danger-soft px-2.5 py-[2px] text-xs font-bold text-danger-on">
                                سررسید شده
                            </span>
                        ) : null}

                        {canEdit && !isPayed && (
                            <button
                                type="button"
                                aria-label={`ویرایش قسط ${number}`}
                                onClick={(event) => {
                                    // The row is the tap target now; reaching
                                    // for the pencil must not also add the
                                    // installment to the payment.
                                    event.stopPropagation()
                                    navigate(`edit/${id}`, {
                                        state: { amount, number },
                                    })
                                }}
                                className="text-text-muted active:scale-90"
                            >
                                <CiEdit className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                </div>

                {/* Its own line rather than a third chip on the one above:
                    «۱۲ روز با تأخیر» next to «پرداخت شده» and a Jalali date
                    is three things competing for ~340px, and the date is the
                    one that gets truncated away. */}
                {isPayed && timing && (
                    <p
                        className={`mt-1 text-[11px] font-bold ${
                            timing.kind === 'early'
                                ? 'text-brand-deep'
                                : 'text-warning-on'
                        }`}
                    >
                        {timing.kind === 'early'
                            ? `${faPlain.format(
                                  timing.days
                              )} روز زودتر از موعد پرداخت شد`
                            : `با ${faPlain.format(
                                  timing.days
                              )} روز تأخیر پرداخت شد`}
                    </p>
                )}

                {/* The manager's own words on how this repayment arrived.
                    They are typed into the «ثبت پرداخت» confirm and were
                    only readable afterwards by leaving the loan for the
                    transactions tab and finding the matching row — so the one
                    screen the repayment is actually looked at was the one
                    screen that did not carry it.

                    Sunken and quoted: it is something a person wrote, not
                    another status the app is asserting about the row. The
                    same note is written onto every installment in a basket,
                    which is why it must stay small — «۳ قسط را نقدی گرفتم»
                    appearing three times has to read as a caption, not as
                    three paragraphs. */}
                {trimmedNote && (
                    <div className="mt-2 rounded-lg bg-sunken px-2 py-1.5">
                        <p
                            className={`text-[11px] leading-5 text-text-body break-words${
                                isLongNote && !showFullNote
                                    ? ' line-clamp-2'
                                    : ''
                            }`}
                        >
                            {/* Inert tags: the loan page has no description
                                search to hand one to, but colouring them keeps
                                a filed note looking filed. */}
                            <HashtagText text={`«${trimmedNote}»`} />
                        </p>
                        {isLongNote && (
                            <span
                                role="button"
                                tabIndex={0}
                                aria-expanded={showFullNote}
                                aria-label={`${
                                    showFullNote ? 'بستن' : 'متن کامل'
                                } توضیح قسط ${faPlain.format(number)}`}
                                onClick={(event) => {
                                    // A paid row is not selectable, but an
                                    // edited-then-unpaid one could be, and
                                    // reading a note must never tick a box.
                                    event.stopPropagation()
                                    toggleNote()
                                }}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' ||
                                        event.key === ' '
                                    ) {
                                        event.preventDefault()
                                        event.stopPropagation()
                                        toggleNote()
                                    }
                                }}
                                className="mt-1 inline-block cursor-pointer text-[10px] font-bold text-brand-deep"
                            >
                                {showFullNote ? 'کمتر' : 'متن کامل'}
                            </span>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}
